import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';
import { useProduct, useProducts } from '@/hooks/useProducts';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface ReviewRow {
  id: string;
  user_name: string;
  rating: number;
  title: string | null;
  body: string;
  verified_purchase: boolean;
  helpful_count: number;
  created_at: string;
}

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const productId = id ? Number(id) : null;
  const { product, loading } = useProduct(productId);
  const { products: allProducts } = useProducts();
  const { user } = useAuth();

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'description' | 'specs' | 'reviews'>('description');
  const [wishlist, setWishlist] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  const [dbReviews, setDbReviews] = useState<ReviewRow[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ user_name: '', user_email: '', rating: 5, title: '', body: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    document.title = `${product.name} - AiShopio Dubai | Buy Online UAE`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `Buy ${product.name} online in Dubai UAE. AED ${product.price}. ${product.description?.slice(0, 100) || ''}. Free delivery across UAE Emirates.`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description || '',
      image: product.image || '',
      sku: String(product.id),
      brand: { '@type': 'Brand', name: 'AiShopio' },
      offers: {
        '@type': 'Offer',
        url: `${siteUrl}/product/${product.id}`,
        priceCurrency: 'AED',
        price: product.price,
        priceValidUntil: '2027-12-31',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'AiShopio' },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviews,
        bestRating: 5,
        worstRating: 1,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Shop', item: `${siteUrl}/shop` },
          { '@type': 'ListItem', position: 3, name: product.category, item: `${siteUrl}/shop?category=${product.category}` },
          { '@type': 'ListItem', position: 4, name: product.name, item: `${siteUrl}/product/${product.id}` },
        ],
      },
    };

    const existing = document.querySelector('script[data-schema="product"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'product');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { document.querySelector('script[data-schema="product"]')?.remove(); };
  }, [product]);

  useEffect(() => {
    if (!productId) return;
    const fetchReviews = async () => {
      setReviewsLoading(true);
      const { data } = await supabase
        .from('reviews')
        .select('id, user_name, rating, title, body, verified_purchase, helpful_count, created_at')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });
      setDbReviews(data || []);
      setReviewsLoading(false);
    };
    fetchReviews();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-loader-4-line text-5xl text-orange-400 animate-spin block mb-4"></i>
            <p className="text-gray-500">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-orange-400 mb-4 block"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Product not found</h2>
          <Link to="/shop" className="px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold cursor-pointer whitespace-nowrap">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const images = [product.image || '', product.image || '', product.image || '', product.image || ''];
  const discount = Math.round(((product.original_price - product.price) / product.original_price) * 100);
  const related = allProducts.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAddToCart = () => {
    if (!product) return;
    const raw = localStorage.getItem('cart');
    const cart = raw ? JSON.parse(raw) : [];
    const existing = cart.find((i: any) => i.id === product.id);
    if (existing) {
      existing.quantity += quantity;
    } else {
      cart.push({ ...product, quantity });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`Hi! I'd like to order:\n\n*${product.name}*\nQty: ${quantity}\nPrice: AED ${product.price * quantity}\n\nPlease confirm availability.`);
    window.open(`https://wa.me/971500000000?text=${msg}`, '_blank');
  };

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewForm.body.trim() || !reviewForm.user_name.trim()) {
      setReviewError('Please fill in your name and review.');
      return;
    }
    setReviewSubmitting(true);
    setReviewError('');
    const { error } = await supabase.from('reviews').insert({
      product_id: product.id,
      user_id: user?.id || '00000000-0000-0000-0000-000000000000',
      user_name: reviewForm.user_name,
      user_email: reviewForm.user_email || null,
      rating: reviewForm.rating,
      title: reviewForm.title || null,
      body: reviewForm.body,
    });
    if (error) {
      setReviewError('Failed to submit review. Please try again.');
    } else {
      setReviewSuccess(true);
      setShowReviewForm(false);
      setReviewForm({ user_name: '', user_email: '', rating: 5, title: '', body: '' });
      const { data } = await supabase
        .from('reviews')
        .select('id, user_name, rating, title, body, verified_purchase, helpful_count, created_at')
        .eq('product_id', product.id)
        .order('created_at', { ascending: false });
      setDbReviews(data || []);
    }
    setReviewSubmitting(false);
  };

  const badgeColors: Record<string, string> = {
    HOT: 'from-red-500 to-orange-500',
    SALE: 'from-orange-500 to-pink-600',
    NEW: 'from-green-500 to-teal-500',
  };

  const avgRating = dbReviews.length > 0
    ? (dbReviews.reduce((s, r) => s + r.rating, 0) / dbReviews.length).toFixed(1)
    : product.rating.toFixed(1);

  const totalReviews = dbReviews.length > 0 ? dbReviews.length : product.reviews;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 86400) return 'Today';
    if (diff < 172800) return '1 day ago';
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    if (diff < 2592000) return `${Math.floor(diff / 604800)} weeks ago`;
    return d.toLocaleDateString('en-AE', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />

      <div className="pt-24 pb-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs sm:text-sm text-gray-500 flex flex-wrap items-center gap-0.5">
            <Link to="/" className="hover:text-orange-500 transition-colors whitespace-nowrap">Home</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <Link to="/shop" className="hover:text-orange-500 transition-colors whitespace-nowrap">Shop</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-800 capitalize whitespace-nowrap">{product?.category}</span>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-800 truncate max-w-[120px] sm:max-w-none">{product?.name}</span>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative rounded-3xl overflow-hidden bg-white/70 backdrop-blur-sm border border-gray-100/50 h-96 md:h-[480px]">
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover object-top transition-all duration-500"
              />
              <span className={`absolute top-4 left-4 text-white text-sm font-bold px-4 py-1.5 rounded-full bg-gradient-to-r ${badgeColors[product.badge]}`}>
                {product.badge}
              </span>
              <span className="absolute top-4 right-4 bg-white/90 text-green-600 text-sm font-bold px-3 py-1.5 rounded-full">
                -{discount}% OFF
              </span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`rounded-2xl overflow-hidden h-20 border-2 transition-all duration-300 cursor-pointer ${
                    selectedImage === i ? 'border-orange-500 scale-105' : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <span className="text-sm font-semibold text-orange-500 capitalize bg-orange-50 px-3 py-1 rounded-full">
                {product.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-black text-gray-800 mt-3 mb-2">{product.name}</h1>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`${i < Math.floor(Number(avgRating)) ? 'ri-star-fill' : 'ri-star-line'} text-amber-400`}></i>
                  ))}
                </div>
                <span className="font-bold text-gray-800">{avgRating}</span>
                <span className="text-gray-500 text-sm">({totalReviews} reviews)</span>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-100">
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                  AED {product.price}
                </span>
                <span className="text-xl text-gray-400 line-through">AED {product.original_price}</span>
              </div>
              <p className="text-green-600 font-semibold text-sm mt-1">
                <i className="ri-price-tag-3-line mr-1"></i>
                You save AED {product.original_price - product.price} ({discount}% off)
              </p>
              <p className="text-xs text-gray-500 mt-1">Price includes 5% VAT</p>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Quantity</label>
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-gray-200 rounded-full overflow-hidden">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-orange-50 hover:text-orange-500 transition-all cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center font-bold text-gray-800">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => q + 1)}
                    className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-orange-50 hover:text-orange-500 transition-all cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <span className="text-sm text-gray-500">Total: <strong className="text-gray-800">AED {product.price * quantity}</strong></span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleAddToCart}
                className={`w-full py-4 rounded-full font-bold text-white transition-all duration-300 hover:scale-[1.02] cursor-pointer whitespace-nowrap ${
                  addedToCart
                    ? 'bg-green-500'
                    : 'bg-gradient-to-r from-orange-500 to-pink-600 hover:shadow-lg hover:shadow-orange-500/30'
                }`}
              >
                {addedToCart ? (
                  <><i className="ri-check-line mr-2"></i>Added to Cart!</>
                ) : (
                  <><i className="ri-shopping-cart-line mr-2"></i>Add to Cart — AED {product.price * quantity}</>
                )}
              </button>

              <button
                onClick={handleWhatsApp}
                className="w-full py-4 rounded-full font-bold text-white transition-all duration-300 hover:scale-[1.02] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #25d366, #128c7e)' }}
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Order via WhatsApp
              </button>

              <button
                onClick={() => setWishlist(!wishlist)}
                className={`w-full py-3 rounded-full font-semibold border-2 transition-all duration-300 hover:scale-[1.02] cursor-pointer whitespace-nowrap ${
                  wishlist
                    ? 'border-pink-500 text-pink-500 bg-pink-50'
                    : 'border-gray-200 text-gray-600 hover:border-pink-400 hover:text-pink-500'
                }`}
              >
                <i className={`${wishlist ? 'ri-heart-fill' : 'ri-heart-line'} mr-2`}></i>
                {wishlist ? 'Saved to Wishlist' : 'Add to Wishlist'}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {[
                { icon: 'ri-shield-check-line', label: '100% Authentic', color: 'text-green-600', bg: 'bg-green-50' },
                { icon: 'ri-truck-line', label: 'Free Dubai Delivery', color: 'text-orange-600', bg: 'bg-orange-50' },
                { icon: 'ri-refresh-line', label: '7-Day Easy Returns', color: 'text-pink-600', bg: 'bg-pink-50' },
                { icon: 'ri-lock-line', label: 'Secure Payment', color: 'text-gray-600', bg: 'bg-gray-50' },
              ].map((badge, i) => (
                <div key={i} className={`flex items-center gap-2 p-2 sm:p-3 rounded-2xl ${badge.bg}`}>
                  <i className={`${badge.icon} ${badge.color} text-base sm:text-lg flex-shrink-0`}></i>
                  <span className="text-xs font-semibold text-gray-700 leading-tight">{badge.label}</span>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-2xl bg-white/70 border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-map-pin-line text-orange-500"></i>
                <span className="text-sm font-semibold text-gray-700">Delivery across all UAE Emirates</span>
              </div>
              <p className="text-xs text-gray-500">Free delivery on orders over AED 200 • Standard 3-5 days • Express available</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-4 sm:p-6 mb-12">
          <div className="overflow-x-auto -mx-1 px-1 mb-6">
            <div className="flex gap-1 bg-gray-100 rounded-full p-1 w-fit min-w-full sm:min-w-0">
              {(['description', 'specs', 'reviews'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer capitalize whitespace-nowrap ${
                    activeTab === tab
                      ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {tab === 'reviews' ? `Reviews (${totalReviews})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'description' && (
            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed text-base">{product.description}</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-award-line', title: 'Premium Quality', desc: 'Carefully selected and quality tested' },
                  { icon: 'ri-truck-line', title: 'Fast Delivery', desc: 'Delivered across all UAE Emirates' },
                  { icon: 'ri-customer-service-line', title: '24/7 Support', desc: 'WhatsApp support always available' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-orange-50/50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white flex-shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm">{item.title}</h4>
                      <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'specs' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(product.specs || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <span className="text-sm font-semibold text-gray-600">{key}</span>
                  <span className="text-sm text-gray-800 font-medium">{String(value)}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="space-y-4">
              {/* Rating Summary */}
              <div className="flex items-center gap-6 p-4 rounded-2xl bg-gradient-to-r from-orange-50 to-pink-50 mb-6">
                <div className="text-center">
                  <div className="text-5xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">{avgRating}</div>
                  <div className="flex items-center gap-0.5 justify-center mt-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`${i < Math.floor(Number(avgRating)) ? 'ri-star-fill' : 'ri-star-line'} text-amber-400 text-sm`}></i>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{totalReviews} reviews</p>
                </div>
                <div className="flex-1 space-y-1">
                  {[5, 4, 3, 2, 1].map(star => {
                    const count = dbReviews.filter(r => r.rating === star).length;
                    const pct = dbReviews.length > 0 ? (count / dbReviews.length) * 100 : (star === 5 ? 75 : star === 4 ? 18 : star === 3 ? 5 : 1);
                    return (
                      <div key={star} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-3">{star}</span>
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-orange-500 to-pink-600 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Write Review Button */}
              {!showReviewForm && !reviewSuccess && (
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="w-full py-3 rounded-2xl border-2 border-dashed border-orange-300 text-orange-500 font-semibold text-sm hover:bg-orange-50 transition-all cursor-pointer"
                >
                  <i className="ri-edit-line mr-2"></i>Write a Review
                </button>
              )}

              {reviewSuccess && (
                <div className="p-4 rounded-2xl bg-green-50 border border-green-200 text-green-700 text-sm font-medium flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  Your review has been submitted! Thank you.
                </div>
              )}

              {/* Review Form */}
              {showReviewForm && (
                <form onSubmit={handleReviewSubmit} className="p-5 rounded-2xl bg-orange-50/50 border border-orange-100 space-y-4">
                  <h3 className="font-bold text-gray-800">Write Your Review</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Your Name *</label>
                      <input
                        type="text"
                        value={reviewForm.user_name}
                        onChange={e => setReviewForm({ ...reviewForm, user_name: e.target.value })}
                        placeholder="Mohammed Al-Rashid"
                        required
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Email (optional)</label>
                      <input
                        type="email"
                        value={reviewForm.user_email}
                        onChange={e => setReviewForm({ ...reviewForm, user_email: e.target.value })}
                        placeholder="email@example.com"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-2 block">Rating *</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                          className="cursor-pointer"
                        >
                          <i className={`${star <= reviewForm.rating ? 'ri-star-fill' : 'ri-star-line'} text-amber-400 text-2xl`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Review Title</label>
                    <input
                      type="text"
                      value={reviewForm.title}
                      onChange={e => setReviewForm({ ...reviewForm, title: e.target.value })}
                      placeholder="Great product!"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Your Review *</label>
                    <textarea
                      value={reviewForm.body}
                      onChange={e => setReviewForm({ ...reviewForm, body: e.target.value })}
                      placeholder="Share your experience with this product..."
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800 resize-none"
                    />
                    <p className="text-xs text-gray-400 text-right mt-1">{reviewForm.body.length}/500</p>
                  </div>
                  {reviewError && <p className="text-red-500 text-sm">{reviewError}</p>}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(false)}
                      className="flex-1 py-2.5 rounded-full border border-gray-200 text-gray-600 text-sm font-medium hover:border-orange-400 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={reviewSubmitting}
                      className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {reviewSubmitting ? <i className="ri-loader-4-line animate-spin"></i> : 'Submit Review'}
                    </button>
                  </div>
                </form>
              )}

              {/* Reviews List */}
              {reviewsLoading ? (
                <div className="text-center py-8">
                  <i className="ri-loader-4-line animate-spin text-orange-400 text-2xl"></i>
                </div>
              ) : dbReviews.length > 0 ? (
                dbReviews.map((review) => (
                  <div key={review.id} className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-800 text-sm">{review.user_name}</span>
                          {review.verified_purchase && (
                            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                              <i className="ri-shield-check-line mr-1"></i>Verified
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <i key={j} className={`${j < review.rating ? 'ri-star-fill' : 'ri-star-line'} text-amber-400 text-xs`}></i>
                          ))}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(review.created_at)}</span>
                    </div>
                    {review.title && <p className="font-semibold text-gray-800 text-sm mb-1">{review.title}</p>}
                    <p className="text-gray-600 text-sm">{review.body}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <i className="ri-chat-3-line text-4xl mb-2 block"></i>
                  <p className="text-sm">No reviews yet. Be the first to review!</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-gray-800 mb-6">
              Related <span className="bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">Products</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {related.map(p => {
                const disc = Math.round(((p.original_price - p.price) / p.original_price) * 100);
                return (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 overflow-hidden hover:border-orange-300 hover:scale-[1.02] transition-all duration-300 group block"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img src={p.image || ''} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                      <span className="absolute top-2 right-2 bg-white/90 text-green-600 text-xs font-bold px-2 py-0.5 rounded-full">-{disc}%</span>
                    </div>
                    <div className="p-3">
                      <h3 className="font-bold text-gray-800 text-xs mb-1 line-clamp-2">{p.name}</h3>
                      <span className="text-sm font-black text-gray-800">AED {p.price}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
